import {format, isSameDay, isSunday} from 'date-fns';
import {useAnimatedPopper} from 'platform/components';
import {foundationTheme, Icon, Show, useDevice} from 'platform/foundation';
import {
  convertStringLocaleToDateFns,
  useDateTimeFormatter,
  useFormatCurrency,
  useLocale,
} from 'platform/locale';
import {cn} from 'platform/shared';

import {Fragment, useState} from 'react';

import {isNotNil} from 'ramda';

import {EMPTY_PLACEHOLDER, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CALENDAR_CONSTANTS, CALENDAR_LINES} from '../constants/calendarConstants';
import type {RentalCalendarRowGroup} from '../types/RentalCalendarRowGroup';
import type {RentalCalendarScale} from '../types/RentalCalendarScale';
import type {RentalResourceCalendarEvent} from '../types/RentalResourceCalendarEvent';
import {RentalCalendarDayChip} from './RentalCalendarDayChip';
import {RentalCalendarEventBar} from './RentalCalendarEventBar';
import {RentalCalendarGroupRow} from './RentalCalendarGroupRow';
import {RentalCalendarSkeletonBars} from './RentalCalendarSkeletonBars';
import {RentalEventTooltip} from './RentalEventTooltip';
import {RentalVehicleTooltip} from './RentalVehicleTooltip';

interface RentalCalendarGridProps extends RequiredTestIdProps {
  scale: RentalCalendarScale;
  groups: RentalCalendarRowGroup[];
  blockReasonLabels: Map<string, string>;
  /** The period's events have not arrived yet, so the tracks show placeholders */
  areEventsPending: boolean;
  onEventClick?: (event: RentalResourceCalendarEvent) => void;
  onSlotClick?: (vehicleId: string, slotStart: Date, slotEnd: Date) => void;
  onDayDrillDown?: (day: Date) => void;
  onVehicleClick?: (vehicleId: string) => void;
}

export function RentalCalendarGrid(props: RentalCalendarGridProps) {
  const device = useDevice();
  const {language} = useLocale();
  const formatDateTime = useDateTimeFormatter();
  const formatCurrency = useFormatCurrency();

  const [hoveredEvent, setHoveredEvent] = useState<RentalResourceCalendarEvent | null>(null);
  const [hoveredVehicleId, setHoveredVehicleId] = useState<string | null>(null);
  const [hoveredColumn, setHoveredColumn] = useState<Date | null>(null);
  const [dragSelection, setDragSelection] = useState<DragSelection | null>(null);

  const isTooltipDisabled = device === 'tablet' || device === 'mobile';

  const {openPopper, closePopper, referenceRef, popperProps, Popper} = useAnimatedPopper({
    placement: 'top',
    gutter: 0,
    isOutsideClickIgnored: true,
  });

  const {
    openPopper: openVehiclePopper,
    closePopper: closeVehiclePopper,
    referenceRef: vehicleReferenceRef,
    popperProps: vehiclePopperProps,
    Popper: VehiclePopper,
  } = useAnimatedPopper({
    placement: 'right',
    gutter: 0,
    isOutsideClickIgnored: true,
  });

  const handleEventMouseEnter = (event: RentalResourceCalendarEvent) => {
    if (isTooltipDisabled) {
      return;
    }
    setHoveredEvent(event);
    openPopper();
  };

  const handleEventMouseLeave = () => {
    if (isTooltipDisabled) {
      return;
    }
    setHoveredEvent(null);
    closePopper();
  };

  const handleVehicleMouseEnter = (vehicleId: string) => {
    if (isTooltipDisabled) {
      return;
    }
    setHoveredVehicleId(vehicleId);
    openVehiclePopper();
  };

  const handleVehicleMouseLeave = () => {
    if (isTooltipDisabled) {
      return;
    }
    setHoveredVehicleId(null);
    closeVehiclePopper();
  };

  const trackWidth = props.scale.columns.length * CALENDAR_CONSTANTS.COLUMN_WIDTH;
  const startDragSelection = (vehicleId: string, columnIndex: number) =>
    setDragSelection({vehicleId, fromIndex: columnIndex, toIndex: columnIndex});

  const extendDragSelection = (vehicleId: string, columnIndex: number) => {
    if (dragSelection?.vehicleId !== vehicleId) {
      return;
    }

    setDragSelection({...dragSelection, toIndex: columnIndex});
  };

  /** A plain click is a one-cell drag, so both paths end here */
  const commitDragSelection = (vehicleId: string) => {
    if (dragSelection?.vehicleId !== vehicleId) {
      return;
    }

    const firstIndex = Math.min(dragSelection.fromIndex, dragSelection.toIndex);
    const lastIndex = Math.max(dragSelection.fromIndex, dragSelection.toIndex);
    const slotStart = props.scale.columns.at(firstIndex);
    // The end is exclusive: the start of the column after the last selected one
    const slotEnd = props.scale.columns.at(lastIndex + 1) ?? props.scale.rangeEnd;

    setDragSelection(null);

    if (isNotNil(slotStart)) {
      props.onSlotClick?.(vehicleId, slotStart, slotEnd);
    }
  };

  const isCellSelected = (vehicleId: string, columnIndex: number) =>
    dragSelection?.vehicleId === vehicleId &&
    columnIndex >= Math.min(dragSelection.fromIndex, dragSelection.toIndex) &&
    columnIndex <= Math.max(dragSelection.fromIndex, dragSelection.toIndex);

  const getColumnHeaderLabel = (column: Date) =>
    props.scale.view === 'month'
      ? format(column, 'EEE', {locale: convertStringLocaleToDateFns(language)})
      : formatDateTime('timeShort', column);

  return (
    <>
      <div
        style={{minWidth: CALENDAR_CONSTANTS.TITLE_COLUMN_WIDTH + trackWidth}}
        data-testid={suffixTestId('resourceView', props)}
      >
        <div className="sticky top-0 z-[14] flex bg-palettes-neutral-10-100">
          <div
            className="sticky left-0 z-[15] shrink-0 bg-palettes-neutral-10-100"
            style={{
              width: CALENDAR_CONSTANTS.TITLE_COLUMN_WIDTH,
              height: CALENDAR_CONSTANTS.HEADER_HEIGHT,
              ...CALENDAR_LINES.headerCorner,
            }}
          />
          <div className="flex" style={CALENDAR_LINES.headerBottom}>
            {props.scale.columns.map((column) => (
              <button
                key={column.toISOString()}
                type="button"
                onMouseEnter={() => setHoveredColumn(column)}
                onMouseLeave={() => setHoveredColumn(null)}
                className={cn(
                  'group/day flex shrink-0 select-none items-center justify-center gap-1 px-1 leading-tight',
                  props.scale.view === 'month' && 'cursor-pointer hover:shadow-elevation-1'
                )}
                // A button's background cannot come from Tailwind here — see CALENDAR_LINES
                style={{
                  width: CALENDAR_CONSTANTS.COLUMN_WIDTH,
                  height: CALENDAR_CONSTANTS.HEADER_HEIGHT,
                  ...(isMajorBoundary(column, props.scale.view)
                    ? CALENDAR_LINES.columnRightMajor
                    : CALENDAR_LINES.columnRight),
                  ...(hoveredColumn?.getTime() === column.getTime() &&
                    props.scale.view === 'month' &&
                    CALENDAR_LINES.hoveredColumn),
                }}
                disabled={props.scale.view === 'day'}
                data-testid={suffixTestId(`column-${column.toISOString()}`, props)}
                onClick={() => props.onDayDrillDown?.(column)}
              >
                <span
                  className={cn(
                    'text-xs font-bold',
                    isSameDay(column, new Date()) && props.scale.view === 'month'
                      ? 'text-warning'
                      : 'text-primary'
                  )}
                >
                  {getColumnHeaderLabel(column)}
                </span>
                <Show when={props.scale.view === 'month'}>
                  <span
                    className={cn(
                      'text-xs',
                      isSameDay(column, new Date()) ? 'font-bold text-warning' : 'text-tertiary'
                    )}
                  >
                    <span className="group-hover/day:underline">{format(column, 'd')}</span>
                  </span>
                </Show>
              </button>
            ))}
          </div>
        </div>

        <div className="relative">
          <div
            className="pointer-events-none absolute inset-y-0 z-0 flex"
            style={{left: CALENDAR_CONSTANTS.TITLE_COLUMN_WIDTH, width: trackWidth}}
          >
            {props.scale.columns.map((column) => (
              <div
                key={column.toISOString()}
                className="h-full shrink-0"
                style={{
                  width: CALENDAR_CONSTANTS.COLUMN_WIDTH,
                  ...(isMajorBoundary(column, props.scale.view)
                    ? CALENDAR_LINES.columnRightMajor
                    : CALENDAR_LINES.columnRight),
                  ...(hoveredColumn?.getTime() === column.getTime() &&
                    CALENDAR_LINES.hoveredColumn),
                }}
              />
            ))}
          </div>

          {props.groups.map((group, groupIndex) => (
            <Fragment key={group.key}>
              <Show when={isNotNil(group.label)}>
                <RentalCalendarGroupRow
                  data-testid={props['data-testid']}
                  label={group.label ?? ''}
                  vehicleCount={group.rows.length}
                  trackWidth={trackWidth}
                />
              </Show>
              {group.rows.map((row, indexInGroup) => {
                // Rows stay numbered across the whole calendar so a group never restarts the count
                const rowIndex = getRowOffset(props.groups, groupIndex) + indexInGroup;

                return (
                  <div
                    key={row.vehicle.vehicleId}
                    className="group flex hover:bg-palettes-neutral-20-100"
                    style={{height: row.rowHeight, ...CALENDAR_LINES.rowBottom}}
                    data-testid={suffixTestId(`resourceView-row-${rowIndex}`, props)}
                  >
                    <div
                      ref={
                        hoveredVehicleId === row.vehicle.vehicleId ? vehicleReferenceRef : undefined
                      }
                      className="sticky left-0 z-[12] flex shrink-0 flex-col justify-center gap-1 overflow-hidden bg-palettes-neutral-10-100 p-2 group-hover:bg-palettes-neutral-20-100"
                      style={{
                        width: CALENDAR_CONSTANTS.TITLE_COLUMN_WIDTH,
                        ...CALENDAR_LINES.titleCell,
                      }}
                      onMouseEnter={() => handleVehicleMouseEnter(row.vehicle.vehicleId)}
                      onMouseLeave={handleVehicleMouseLeave}
                    >
                      <button
                        type="button"
                        onClick={() => props.onVehicleClick?.(row.vehicle.vehicleId)}
                        className="cursor-pointer bg-transparent p-0 text-left text-sm font-bold text-primary hover:text-link hover:underline"
                        data-testid={suffixTestId(`resourceView-vehicle-${rowIndex}`, props)}
                      >
                        <span className="block truncate">{row.vehicle.title}</span>
                      </button>
                      <div className="flex select-none items-center gap-1">
                        <Icon size={4} value="sidebar/vehicles" color="palettes.black.900.70" />
                        <span className="truncate text-xs text-secondary">
                          {row.vehicle.registrationPlate ?? EMPTY_PLACEHOLDER}
                        </span>
                        <Icon size={4} value="action/payment" color="palettes.black.900.70" />
                        <span className="truncate text-xs text-secondary">
                          {isNotNil(row.vehicle.dailyRate)
                            ? formatCurrency(
                                parseFloat(row.vehicle.dailyRate.amount),
                                row.vehicle.dailyRate.currency
                              )
                            : EMPTY_PLACEHOLDER}
                        </span>
                      </div>
                    </div>

                    <div
                      className="relative"
                      style={{width: trackWidth}}
                      onPointerUp={() => commitDragSelection(row.vehicle.vehicleId)}
                      onPointerLeave={() => setDragSelection(null)}
                    >
                      <div className="absolute inset-0 flex">
                        {props.scale.columns.map((column, columnIndex) => (
                          <div
                            key={column.toISOString()}
                            className="group/cell flex h-full shrink-0 cursor-pointer items-center justify-center hover:bg-palettes-neutral-30-100"
                            // The hover background would paint over the overlay line, so the cell draws its own
                            style={{
                              width: CALENDAR_CONSTANTS.COLUMN_WIDTH,
                              ...(isMajorBoundary(column, props.scale.view)
                                ? CALENDAR_LINES.columnRightMajor
                                : CALENDAR_LINES.columnRight),
                              ...(isCellSelected(row.vehicle.vehicleId, columnIndex) &&
                                CALENDAR_LINES.selectedCell),
                            }}
                            onPointerDown={() =>
                              startDragSelection(row.vehicle.vehicleId, columnIndex)
                            }
                            onPointerEnter={() =>
                              extendDragSelection(row.vehicle.vehicleId, columnIndex)
                            }
                          >
                            <span className="opacity-0 transition-opacity group-hover/cell:opacity-100">
                              <Icon size={4} value="content/add" color="palettes.black.900.70" />
                            </span>
                          </div>
                        ))}
                      </div>
                      {/*
                        The wrapper spans the whole row, so it must never take pointer events
                        itself — the cells beneath it are what start a click or a drag. Only its
                        children opt back in, and while this row is being dragged they opt out
                        again so a drag crossing a bar keeps extending over the cells.
                      */}
                      <div
                        className={cn(
                          'pointer-events-none absolute inset-0',
                          dragSelection?.vehicleId !== row.vehicle.vehicleId &&
                            '[&>*]:pointer-events-auto'
                        )}
                      >
                        <Show when={props.areEventsPending}>
                          <RentalCalendarSkeletonBars
                            data-testid={props['data-testid']}
                            rowIndex={rowIndex}
                            trackWidth={trackWidth}
                          />
                        </Show>
                        {row.groups.map((dayGroup) => (
                          <RentalCalendarDayChip
                            key={`dayChip-${dayGroup.columnIndex}`}
                            data-testid={props['data-testid']}
                            day={dayGroup.day}
                            columnIndex={dayGroup.columnIndex}
                            laneIndex={dayGroup.laneIndex}
                            events={dayGroup.events}
                            onDayDrillDown={props.onDayDrillDown}
                          />
                        ))}
                        {row.events.map((event, eventIndex) => (
                          <RentalCalendarEventBar
                            key={event.id}
                            data-testid={props['data-testid']}
                            event={event}
                            eventIndex={eventIndex}
                            scale={props.scale}
                            trackWidth={trackWidth}
                            collisionLayout={row.collisionLayout}
                            blockReasonLabels={props.blockReasonLabels}
                            isHovered={hoveredEvent?.id === event.id}
                            referenceRef={referenceRef}
                            onEventClick={props.onEventClick}
                            onEventMouseEnter={handleEventMouseEnter}
                            onEventMouseLeave={handleEventMouseLeave}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>

      <Popper
        {...popperProps}
        usePortal
        floatingStyles={{...popperProps.floatingStyles, zIndex: foundationTheme.zIndices.TOOLTIP}}
      >
        <Show when={hoveredEvent}>
          <RentalEventTooltip
            data-testid={suffixTestId('body', props)}
            event={hoveredEvent}
            blockReasonLabels={props.blockReasonLabels}
          />
        </Show>
      </Popper>
      <VehiclePopper
        {...vehiclePopperProps}
        usePortal
        floatingStyles={{
          ...vehiclePopperProps.floatingStyles,
          zIndex: foundationTheme.zIndices.TOOLTIP,
        }}
      >
        <Show when={hoveredVehicleId}>
          <RentalVehicleTooltip
            data-testid={suffixTestId('body', props)}
            vehicleId={hoveredVehicleId}
          />
        </Show>
      </VehiclePopper>
    </>
  );
}

/** Week ends after Sunday, and the day is read in blocks of hours — both deserve a stronger line */
const isMajorBoundary = (column: Date, view: RentalCalendarScale['view']) =>
  view === 'month'
    ? isSunday(column)
    : (column.getHours() + 1) % CALENDAR_CONSTANTS.MAJOR_HOUR_STEP === 0; // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible

/** Rows before this group, so numbering continues rather than restarting per group */
const getRowOffset = (groups: RentalCalendarRowGroup[], groupIndex: number) =>
  groups.slice(0, groupIndex).reduce((total, group) => total + group.rows.length, 0);

interface DragSelection {
  vehicleId: string;
  fromIndex: number;
  toIndex: number;
}
